import { useCallback } from "react";
import { FiExternalLink, FiDownload } from "react-icons/fi";
import { Spinner } from "@chakra-ui/react";
import { cn } from "utils/helpers";
import { useRfxIndividualResponseExport } from "newui/features/RfxReports";
import type { SupplierEvaluationSummary, SupplierEvaluationStatus } from "../../../types/scoring.type";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";

interface ScoringModalHeaderProps {
	readOnly?: boolean;
	rfxId: number;
	selectedInviteId: number;
	suppliers: SupplierEvaluationSummary[];
}

const getStatusLabel = (status: SupplierEvaluationStatus): string => {
	switch (status) {
		case "done":
			return "Done";
		case "in_progress":
			return "In Progress";
		case "not_started":
			return "Not Started";
		default:
			return status;
	}
};

const getStatusBadgeClasses = (status: SupplierEvaluationStatus): string => {
	switch (status) {
		case "done":
			return "bg-green-100 text-green-700";
		case "in_progress":
			return "bg-blue-100 text-blue-700";
		case "not_started":
			return "bg-gray-100 text-gray-600";
		default:
			return "bg-gray-100 text-gray-600";
	}
};

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) {
		return "—";
	}
	return new Date(dateStr).toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

export const ScoringModalHeader = ({
	suppliers,
	selectedInviteId,
	rfxId,
}: ScoringModalHeaderProps) => {
	const currentSupplier = suppliers.find((s) => s.inviteId === selectedInviteId);
	const { clientPaths } = useNavigationPaths();
	const { mutate: exportResponse, isPending: isPdfLoading } = useRfxIndividualResponseExport();

	const handlePdfDownload = useCallback(() => {
		exportResponse({ rfxId, inviteId: selectedInviteId });
	}, [rfxId, selectedInviteId, exportResponse]);

	return (
		<div className="space-y-3" data-testid="scoring-modal-header">
			<div className="flex flex-wrap items-center gap-4">
				{currentSupplier && (
					<>
						<span
							className={cn(
								"inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
								getStatusBadgeClasses(currentSupplier.status)
							)}
						>
							{getStatusLabel(currentSupplier.status)}
						</span>

						<span className="text-xs text-gray-500">
							Started: {formatDate(currentSupplier.startedAt)}
						</span>
						<span className="text-xs text-gray-500">
							Completed: {formatDate(currentSupplier.completedAt)}
						</span>
					</>
				)}

				<div className="ml-auto flex items-center gap-2">
					<a
						href={clientPaths.viewRfxSubmission(rfxId, selectedInviteId)}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
						data-testid="view-response-link"
					>
						<FiExternalLink size={12} />
						View Response
					</a>
					<button
						type="button"
						onClick={handlePdfDownload}
						disabled={isPdfLoading}
						className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						data-testid="download-pdf-btn"
					>
						{isPdfLoading ? (
							<Spinner size="xs" />
						) : (
							<FiDownload size={12} />
						)}
						{isPdfLoading ? "Generating..." : "PDF"}
					</button>
				</div>
			</div>
		</div>
	);
};
